import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Flame, MoreVertical, Play, Pause, Pencil, Archive, Trash2, SkipForward, NotebookPen, Bell, BellOff } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { fireConfetti } from './Confetti';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { HabitReminderDialog } from './HabitReminderDialog';

const toIconKey = (name) =>
  (name || 'sparkles')
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');

const fmtSeconds = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

export const HabitItem = ({ habit, log, streak, onLog, onEdit, onArchive, onDelete }) => {
  const IconCmp = LucideIcons[toIconKey(habit.icon)] || LucideIcons.Sparkles;
  const isDone =
    log &&
    (log.status === 'done' ||
      (habit.type !== 'binary' && Number(log.value || 0) >= Number(habit.target_value || 1)));
  const isSkipped = log && log.status === 'skipped';

  // Quantitative inline input state
  const [qty, setQty] = useState(log?.value ?? 0);
  useEffect(() => { setQty(log?.value ?? 0); }, [log?.value]);

  // Timer state for time habits
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(Number(log?.value || 0) * 60);
  const tickRef = useRef(null);
  useEffect(() => { setSeconds(Number(log?.value || 0) * 60); }, [log?.value]);
  useEffect(() => {
    if (running) {
      tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else if (tickRef.current) clearInterval(tickRef.current);
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [running]);

  const handleBinaryToggle = async () => {
    const next = isDone ? 'undone' : 'done';
    const wasDone = isDone;
    await onLog({ status: next === 'done' ? 'done' : 'missed', value: next === 'done' ? 1 : 0 });
    if (!wasDone) fireConfetti();
  };

  const handleQtyCommit = async (val) => {
    const v = Math.max(0, Number(val) || 0);
    setQty(v);
    const status = v >= Number(habit.target_value || 1) ? 'done' : v > 0 ? 'partial' : 'missed';
    const wasDone = isDone;
    await onLog({ status, value: v });
    if (!wasDone && status === 'done') fireConfetti();
  };

  const handleTimerStop = async () => {
    setRunning(false);
    const minutes = Math.round(seconds / 60);
    const status = minutes >= Number(habit.target_value || 1) ? 'done' : minutes > 0 ? 'partial' : 'missed';
    const wasDone = isDone;
    await onLog({ status, value: minutes });
    if (!wasDone && status === 'done') fireConfetti();
  };

  const [noteOpen, setNoteOpen] = useState(false);
  const [noteVal, setNoteVal] = useState(log?.note || '');
  const [moodVal, setMoodVal] = useState(log?.mood || '');
  const [reminderOpen, setReminderOpen] = useState(false);
  useEffect(() => { setNoteVal(log?.note || ''); setMoodVal(log?.mood || ''); }, [log?.note, log?.mood]);
  const saveNote = async () => {
    await onLog({ status: log?.status || 'done', value: log?.value ?? 0, note: noteVal, mood: moodVal });
    setNoteOpen(false);
  };

  return (
    <div
      className={`group relative flex items-center gap-4 border-b border-border px-3 sm:px-4 py-4 transition-colors hover:bg-muted/40 ${
        isDone ? 'opacity-80' : ''
      }`}
      data-testid={`habit-item-${habit.id}`}
    >
      {/* Color accent */}
      <span
        className="hidden sm:block absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ backgroundColor: habit.color }}
        aria-hidden="true"
      />
      <div
        className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${habit.color}1F`, color: habit.color }}
        aria-hidden="true"
      >
        <IconCmp className="h-5 w-5" strokeWidth={1.6} />
      </div>

      <div className="min-w-0 flex-1">
        <Link to={`/habits/${habit.id}`} className="block">
          <div className="flex items-center gap-2">
            <h4 className={`font-heading font-medium truncate ${isDone ? 'line-through text-muted-foreground' : ''}`}>{habit.name}</h4>
            {streak > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-secondary" data-testid={`habit-streak-${habit.id}`}>
                <Flame className="h-3 w-3" strokeWidth={2} />
                {streak}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground capitalize">
            {habit.type === 'binary' ? 'Check-in' : `${habit.target_value}${habit.unit ? ' ' + habit.unit : habit.type === 'time' ? ' min' : ''}`}
            {isSkipped && ' · Skipped'}
          </div>
        </Link>
      </div>

      {/* Type-adapted control */}
      <div className="flex items-center gap-2">
        {habit.type === 'binary' && (
          <motion.button
            type="button"
            onClick={handleBinaryToggle}
            whileTap={{ scale: 0.85 }}
            className={`h-10 w-10 rounded-full border-2 flex items-center justify-center transition-colors ${
              isDone
                ? 'border-transparent text-primary-foreground'
                : 'border-border bg-background hover:border-primary'
            }`}
            style={isDone ? { backgroundColor: habit.color } : undefined}
            data-testid={`habit-toggle-${habit.id}`}
            aria-label={isDone ? 'Mark not done' : 'Mark done'}
            aria-pressed={isDone}
          >
            <AnimatePresence>
              {isDone && (
                <motion.span
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  <Check className="h-5 w-5" strokeWidth={3} />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        )}

        {habit.type === 'quantitative' && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleQtyCommit(Math.max(0, Number(qty || 0) - 1))}
              className="h-9 w-9 rounded-full bg-muted text-foreground hover:bg-border"
              data-testid={`habit-qty-dec-${habit.id}`}
              aria-label="Decrease"
            >−</button>
            <input
              type="number"
              value={qty}
              min={0}
              onChange={(e) => setQty(e.target.value)}
              onBlur={(e) => handleQtyCommit(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleQtyCommit(e.currentTarget.value)}
              className="w-14 h-9 text-center bg-muted rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid={`habit-qty-input-${habit.id}`}
            />
            <button
              type="button"
              onClick={() => handleQtyCommit(Number(qty || 0) + 1)}
              className="h-9 w-9 rounded-full text-primary-foreground hover:opacity-90"
              style={{ backgroundColor: habit.color }}
              data-testid={`habit-qty-inc-${habit.id}`}
              aria-label="Increase"
            >+</button>
            <span className="text-[10px] text-muted-foreground hidden sm:block ml-1">/ {habit.target_value}</span>
          </div>
        )}

        {habit.type === 'time' && (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm tabular-nums w-14 text-right" data-testid={`habit-timer-${habit.id}`}>{fmtSeconds(seconds)}</span>
            {!running ? (
              <button
                type="button"
                onClick={() => setRunning(true)}
                className="h-9 w-9 rounded-full text-primary-foreground hover:opacity-90"
                style={{ backgroundColor: habit.color }}
                data-testid={`habit-timer-start-${habit.id}`}
                aria-label="Start timer"
              >
                <Play className="h-4 w-4 mx-auto" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleTimerStop}
                className="h-9 w-9 rounded-full bg-secondary text-secondary-foreground hover:opacity-90"
                data-testid={`habit-timer-stop-${habit.id}`}
                aria-label="Stop and log"
              >
                <Pause className="h-4 w-4 mx-auto" />
              </button>
            )}
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="h-9 w-9 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="More options"
              data-testid={`habit-menu-${habit.id}`}
            >
              <MoreVertical className="h-4 w-4 mx-auto" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" data-testid={`habit-menu-content-${habit.id}`}>
            <DropdownMenuItem onClick={() => setNoteOpen(true)} data-testid={`habit-action-note-${habit.id}`}>
              <NotebookPen className="h-4 w-4 mr-2" /> Add note / mood
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setReminderOpen(true)} data-testid={`habit-action-reminder-${habit.id}`}>
              {habit.reminder_time
                ? <><Bell className="h-4 w-4 mr-2" /> Reminder · {habit.reminder_time}</>
                : <><Bell className="h-4 w-4 mr-2" /> Set reminder</>}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onLog({ status: 'skipped', value: 0 })} data-testid={`habit-action-skip-${habit.id}`}>
              <SkipForward className="h-4 w-4 mr-2" /> Skip today
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(habit)} data-testid={`habit-action-edit-${habit.id}`}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onArchive(habit)} data-testid={`habit-action-archive-${habit.id}`}>
              <Archive className="h-4 w-4 mr-2" /> {habit.archived_at ? 'Restore' : 'Archive'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDelete(habit)} className="text-destructive" data-testid={`habit-action-delete-${habit.id}`}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Reminder dialog (shared) */}
      <HabitReminderDialog habit={habit} open={reminderOpen} onOpenChange={setReminderOpen} />

      {/* Note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent data-testid={`habit-note-dialog-${habit.id}`}>
          <DialogHeader>
            <DialogTitle>Reflection</DialogTitle>
            <DialogDescription>Capture how today&apos;s habit felt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Mood</label>
              <div className="flex gap-2 mt-2">
                {['😄', '🙂', '😐', '😕', '😞'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMoodVal(m)}
                    className={`h-10 w-10 rounded-full text-xl transition-transform ${moodVal === m ? 'bg-muted scale-110 ring-2 ring-primary' : 'hover:bg-muted'}`}
                    data-testid={`habit-mood-${habit.id}-${m}`}
                    aria-label={`Mood ${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Note</label>
              <Textarea value={noteVal} onChange={(e) => setNoteVal(e.target.value)} placeholder="A short reflection..." className="mt-2" data-testid={`habit-note-input-${habit.id}`} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNoteOpen(false)}>Cancel</Button>
              <Button onClick={saveNote} data-testid={`habit-note-save-${habit.id}`}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
